import React, { useEffect, useState } from 'react';
import { MainLayout } from '../components/MainLayout';
import { Button } from '../components/Button';
import { getSessions, revokeSession, revokeAllOtherSessions } from '../api/security';
import { Shield, Laptop } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function Sessions() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadSessions = async () => {
      try {
        const data = await getSessions();
        if (!cancelled) {
          setSessions(data);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadSessions();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleRevoke = async (id) => {
    setBusyId(id);
    try {
      await revokeSession(id);
      setSessions((current) => current.filter((session) => session.id !== id));
    } finally {
      setBusyId('');
    }
  };

  const handleRevokeOthers = async () => {
    setBusyId('all');
    try {
      await revokeAllOtherSessions();
      const refreshed = await getSessions();
      setSessions(refreshed);
    } finally {
      setBusyId('');
    }
  };

  return (
    <MainLayout>
      <div className="page-header notes-hero-banner">
        <div className="notes-hero-content">
          <div>
            <h1 className="notes-hero-title">Sessions</h1>
            <p className="notes-hero-subtitle">Manage active sessions for {user?.email}</p>
          </div>
          <Button variant="ghost" className="notes-hero-btn-secondary" onClick={handleRevokeOthers} loading={busyId === 'all'}>
            Revoke Other Sessions
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '240px', color: 'var(--text-secondary)', gap: '12px' }}>
          Loading sessions...
        </div>
      ) : (
        <div className="item-grid">
          {sessions.length === 0 ? (
            <div className="glass-panel" style={{ padding: '32px', color: 'var(--text-secondary)' }}>
              No active sessions found.
            </div>
          ) : sessions.map((session) => (
            <div key={session.id} className="glass-panel vault-item" style={{ display: 'grid', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                  <div style={{ background: 'rgba(59, 130, 246, 0.1)', padding: '12px', borderRadius: '12px', color: 'var(--accent-primary)' }}>
                    <Laptop size={24} />
                  </div>
                  <div>
                    <div className="item-title">{session.device_name || 'Unknown device'}</div>
                    <div className="item-subtitle">{session.ip_address} • Started {new Date(session.created_at).toLocaleString()}</div>
                  </div>
                </div>
                {session.is_current ? (
                  <div className="chip success">
                    <Shield size={14} /> Current
                  </div>
                ) : null}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                {session.expires_at && new Date(session.expires_at).getFullYear() > 2090
                  ? 'Permanent • Active until signed out'
                  : `Expires ${session.expires_at ? new Date(session.expires_at).toLocaleString() : 'Permanent'}`}
              </div>
              <div>
                <Button variant="danger" onClick={() => handleRevoke(session.id)} loading={busyId === session.id}>
                  Revoke Session
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </MainLayout>
  );
}
